import * as React from 'react'
import { FormEvent, useEffect, useState } from 'react'
import { createGuid, equals } from '../../guids'
import '../../ManageClients.css'
import { byClientCode, difference, validateCumulativeOrderValueLimitInJPY, validateVirtualServerId } from '../../utils'
import { getAllClients, getActiveClients, postAddClient, putClientUpdate } from '../../web/clients';
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../actions";
import { actionCreators, displayToasts } from "../../actions";
import { ClientAlert, ClientAlertForm, ClientConfig, ClientForm, ContactInput, createBlankClientForm, generateFormFromClient, ManageContactsForm, VirtualServer, VirtualServerInput } from "../../types/clients";
import { LimitType, Model, Severity, Toast } from "../../types/types";
import ClientsList from "./ClientsList";
import ClientInfo from "./ClientInfo";
import CumulativeLimitGrid from "./CumulativeLimitGrid";
import RCGrid from "./RCGrid";
import ContactsForm from "./ContactsForm";
import VirtualServersGrid from "./VirtualServersGrid";
import { ManageContactsButton } from "./ManageContactsButton";

export default function ManageClientsForm(): JSX.Element {

  const [clientConfigs, setClientConfigs] = useState<ReadonlyArray<ClientConfig>>([]);
  const [clientForms, setClientForms] = useState<ReadonlyArray<ClientForm>>([]);
  const [addingClient, _setAddingClient] = useState<boolean>(false);
  const selectedClientCode = useSelector<Model, string | undefined>(s => s.selectedClientCode);
  const dispatch = useDispatch();

  const clientForm = clientForms.find(c => c.originalClientCode === selectedClientCode && (c.originalClientCode !== undefined || addingClient));
  const [forceUpdateExchangeLimit, setForceUpdateExchangeLimit] = useState(false);

  function setClientForm(clientForm: ClientForm, saved: boolean = false) {
    let updatedForms: ClientForm[] | undefined;

    if (clientForms.some(c => c.originalClientCode === clientForm.originalClientCode)) {
      updatedForms = clientForms.map(c => c.originalClientCode === clientForm.originalClientCode
        ? {...clientForm, saveState: saved ? 'Saved' : 'Dirty'}
        : c);
    } else {
      updatedForms = [...clientForms, clientForm];
    }

    setClientForms([...updatedForms]);
  }

  function setAddingClient(adding: boolean) {
    dispatch(actionCreators.selectClient(undefined));
    _setAddingClient(adding);

    if (adding && !clientForms.some(f => f.originalClientCode === undefined)) {
      setClientForm(createBlankClientForm());
    }
  }

  function clientHasExchangeOrVirtualServerChanges(clientFormState: ClientForm): boolean {
    function hasAnyNewVirtualServers(client: ClientConfig, clientForm: ClientForm): boolean {
      const newServerIds = [...clientForm.virtualServers].map(vs => vs.serverId)
      const oldServerIds = [...client.virtualServers].map(vs => vs.serverId)
      return difference(newServerIds, oldServerIds).size > 0
    }

    if (addingClient) {
      return clientFormState.virtualServers.size > 0;
    }

    const originalClient = clientConfigs.find(c => c.originalId === clientFormState?.originalId);

    if (originalClient === undefined) {
      throw new Error("Original client not found when updating existing client");
    }

    const anyExchangeLimitHasChanged =
      clientFormState.tcsLimitInJPY !== originalClient.tcsLimitInJPY.toString() ||
      clientFormState.cumulativeOrderValueLimitInJPY !== originalClient.cumulativeOrderValueLimitInJPY.toString() ||
      clientFormState.cumulativeOrderValueTimeIntervalSeconds !== originalClient.cumulativeOrderValueTimeIntervalSeconds.toString() ||
      (originalClient.cumulativeExecutionValueLimitInJPY !== undefined &&
        clientFormState.cumulativeExecutionValueLimitInJPY !== originalClient.cumulativeExecutionValueLimitInJPY.toString()) ||
      (originalClient.cumulativeExecutionValueTimeIntervalSeconds !== undefined &&
        clientFormState.cumulativeExecutionValueTimeIntervalSeconds !== originalClient.cumulativeExecutionValueTimeIntervalSeconds.toString()) ||
      (originalClient.realisticExposureLimit !== undefined &&
        clientFormState.realisticExposureLimit !== originalClient.realisticExposureLimit.toString());


    const hasATCSLimit = !!clientFormState.tcsLimitInJPY.toString()

    const hasAnyVirtualServers =
      clientFormState.virtualServers.size > 0

    return (hasATCSLimit && hasAnyVirtualServers && clientFormState.originalClientCode === undefined)
      || hasAnyNewVirtualServers(originalClient, clientFormState) || anyExchangeLimitHasChanged;
  }

  const loadClients = async () => {
    const clients = await getAllClients();
    setClientConfigs(clients);

    // Exclude inactive clients from redux store update
    const activeClients = clients.filter(c => c.isActive);
    dispatch(actionCreators.setClientSettings(activeClients));
  }

  useEffect(() => {
    loadClients();
  }, [])

  useEffect(() => {
    if (clientConfigs !== undefined && selectedClientCode !== undefined) {
      const selectedClient: ClientConfig | undefined = clientConfigs.find(c => c.code === selectedClientCode);

      if (selectedClient && !clientForms.some(f => f.originalClientCode === selectedClientCode)) {
        setClientForms([...clientForms, generateFormFromClient(selectedClient)])
      }
    }
  }, [clientConfigs, selectedClientCode]);

  function checkCumulativeLimitValues(cumulativeOrderValueLimitInJPY: string, clientForm: ClientForm) {
    if(!validateCumulativeOrderValueLimitInJPY(cumulativeOrderValueLimitInJPY)) {
      dispatch(
        actions.displayToasts([
          {
            content: 'Cumulative order value limit must be a positive number',
            header: 'Invalid Cumulative Order Value Limit',
            id: createGuid(),
          },
        ]),
      )
    return false;
    }
  }

  function addVirtualServerClicked(newVirtualServerIdInput: string, clientForm: ClientForm) {
    if (!validateVirtualServerId(newVirtualServerIdInput)) {
      dispatch(
        actions.displayToasts([
          {
            content: 'Virtual server ids must be 6 alphanumeric characters',
            header: 'Invalid Virtual Server Id',
            id: createGuid(),
          },
        ]),
      )
      return false;
    }

    // Ignore attempts to duplicate machines ids
    if (
      [...clientForm.virtualServers].filter(
        v => v.serverId === newVirtualServerIdInput,
      ).length > 0
    ) {
      dispatch(
        actions.displayToasts([
          {
            content: 'Virtual Server Id already exists',
            header: 'Invalid Virtual Server Id',
            id: createGuid(),
          },
        ]),
      )
      return false;
    }

    const newVirtualServers: ReadonlyArray<VirtualServerInput> = newVirtualServerIdInput
      .split(',')
      .map(value => value.trim())
      .filter(value => value !== '')
      .map(value => {
        return {
          id: createGuid(),
          serverId: value,
        }
      })

    const virtualServersSet = new Set([
      ...clientForm.virtualServers,
      ...newVirtualServers,
    ])

    setClientForm({
      ...clientForm,
      virtualServers: virtualServersSet,
    });

    return true;
  }

  async function clientFormSubmitted(clientForm: ClientForm) {
    const virtualServers: ReadonlySet<VirtualServer> = new Set(
      [...clientForm.virtualServers].map(value => {
        return { id: value.id, serverId: value.serverId }
      }),
    )
    const alerts: ReadonlyArray<ClientAlert> = clientForm.alerts.map(
      alert => {
        return {
          contacts: new Set(
            [...alert.contacts].map(contact => {
              return { id: contact.id, emailAddress: contact.emailAddress }
            }),
          ),
          id: alert.id,
          limitType: alert.limitType,
          thresholdInPercent: parseFloat(alert.thresholdInPercent),
        }
      },
    )

    const pendingClient: ClientConfig = {
      alerts,
      code: clientForm.code,
      exposureLimitInJpy: parseFloat(clientForm.exposureLimitInJpy),
      id: createGuid(),
      originalId: clientForm.originalId,
      name: clientForm.name,
      isActive: clientForm.isActive,
      tcsLimitInJPY: parseFloat(clientForm.tcsLimitInJPY),
      unactionedOrdersLimit: parseFloat(clientForm.unactionedOrdersLimit),
      realisticExposureLimit: parseFloat(clientForm.realisticExposureLimit),
      virtualServerGroupId: clientForm.virtualServerGroupId,
      virtualServers,
      cumulativeOrderValueLimitInJPY: parseFloat(
        clientForm.cumulativeOrderValueLimitInJPY,
      ),
      cumulativeOrderValueTimeIntervalSeconds: parseInt(
        clientForm.cumulativeOrderValueTimeIntervalSeconds,
        10,
      ),
      cumulativeExecutionValueLimitInJPY:
        !!clientForm.cumulativeExecutionValueLimitInJPY
          ? parseFloat(clientForm.cumulativeExecutionValueLimitInJPY)
          : undefined,
      cumulativeExecutionValueTimeIntervalSeconds:
        !!clientForm.cumulativeExecutionValueLimitInJPY
          ? parseInt(clientForm.cumulativeExecutionValueTimeIntervalSeconds, 10)
          : undefined,
    }

    setClientForm({
      ...clientForm,
      awaitingSaveResult: true,
      originalClientCode: clientForm.code,
    })

    let result: { toasts: Toast[], success: boolean } | undefined;

    if (addingClient) {
      result = await postAddClient(pendingClient);
    } else {
      const originalClientId = clientConfigs.find(s => s.code === clientForm.originalClientCode)?.id;

      if (!originalClientId) {
        throw new Error("Original client id expected on client but was not found");
      }

      result = await putClientUpdate(pendingClient, originalClientId, forceUpdateExchangeLimit);
    }

    dispatch(displayToasts(result.toasts))

    if (result.success) {
      dispatch(actions.selectClient(clientForm.code))
      setClientForm(generateFormFromClient(pendingClient), result.success)
      if (addingClient) {
        setAddingClient(false);
        setClientConfigs([...clientConfigs, pendingClient].sort(byClientCode))
      } else {
        setClientConfigs(clientConfigs.map(c => c.code === clientForm.originalClientCode ? pendingClient : c));
      }
      dispatch(actionCreators.selectClient(clientForm.code))

      const clientSettings = await getActiveClients();
      dispatch(actionCreators.setClientSettings(clientSettings));
    } else {
      setClientForm({
        ...clientForm,
        awaitingSaveResult: false,
      }, result.success)
    }
    setForceUpdateExchangeLimit(false);
  }

  function handleAddContactClicked(emailAddressInput: string, clientForm: ClientForm, manageContactsForm: ManageContactsForm) {
    if (
      emailAddressInput === ''
    ) {
      return false;
    }

    if (
      [...manageContactsForm.associatedAlertForm.contacts].filter(
        c => c.emailAddress === emailAddressInput,
      ).length > 0
    ) {
      dispatch(
        actions.displayToasts([
          {
            content: 'Email address already exists',
            header: 'Duplicate Contact',
            id: createGuid(),
          },
        ]),
      )
      return false;
    }

    const newContacts = emailAddressInput
      .split(',')
      .map(value => value.trim())
      .filter(value => value !== '')
      .map(value => {
        return {
          emailAddress: value,
          id: createGuid(),
        }
      })

    const contacts: ReadonlySet<ContactInput> = new Set([
      ...manageContactsForm.associatedAlertForm.contacts,
      ...newContacts,
    ])

    const updatedManageContactsForm = {
      ...manageContactsForm,
      associatedAlertForm: {
        ...manageContactsForm.associatedAlertForm,
        contacts,
      },
      emailAddressInput: '',
    }

    const alert = manageContactsForm.associatedAlertForm
    const alerts = clientForm.alerts.map(value => {
      if (alert.id === value.id) {
        return {
          ...value,
          contacts,
        }
      }

      return value
    })

    setClientForm({
      ...clientForm,
      manageContactsForm: updatedManageContactsForm,
      alerts
    });

    return true;
  }

  function removeContactClicked(clientForm: ClientForm, manageContactsForm: ManageContactsForm, buttonValue: string) {
    const associatedAlertForm = manageContactsForm.associatedAlertForm
    const alerts = clientForm.alerts.map(alert => {
      return {
        ...alert,
        contacts: new Set(
          [...alert.contacts].filter(
            contact => !equals(contact.id, buttonValue),
          ),
        ),
      }
    })

    const updatedManageContactsForm: ManageContactsForm = {
      ...manageContactsForm,
      associatedAlertForm: {
        ...associatedAlertForm,
        contacts: new Set(
          [...associatedAlertForm.contacts].filter(
            value => !equals(value.id, buttonValue),
          ),
        ),
      },
    }

    setClientForm({
      ...clientForm,
      alerts,
      manageContactsForm: updatedManageContactsForm
    });
  }

  const selectSomethingMessage = (
    <p>
      <em>Select a client to manage their limits.</em>
    </p>
  )

  const gridClass: string =
    (clientForm?.manageContactsForm !== undefined
      ? 'manage-clients-grid-with-contacts'
      : 'manage-clients-grid') +
    ' theme--black-on-white theme--black-on-white--no-solid-backgrounds'

  function displayManageContactsOverlay(clientForm: ClientForm, associatedAlertForm: ClientAlertForm, severity: Severity) {
    setClientForm({
      ...clientForm,
      manageContactsForm: {
        associatedAlertForm,
        severity
      }
    });
  }

  function getClientForm(clientForm: ClientForm): JSX.Element {
    function getAlertsForRC(alerts: ReadonlyArray<ClientAlertForm>, limitType: LimitType): ReadonlyArray<ClientAlertForm> {
      return alerts.filter(value => {
        return value.limitType === limitType
      })
    }

    function alertChanged(alert: ClientAlertForm, alertValue: string) {
      const updatedAlerts = clientForm.alerts.map(value => {
        if (alert.id === value.id) {
          if (alert.limitType !== "Realistic_exposure_value") {
            return {
              ...value,
              thresholdInPercent: Math.min(
                parseFloat(alertValue),
                99.9,
              ).toString(),
            }
          } else {
            return {
              ...value,
              thresholdInPercent: parseFloat(alertValue).toString(),
            }
          }
        }

        return value
      })

      setClientForm({
        ...clientForm,
        alerts: updatedAlerts,
      });
    }

    function removeVirtualServerClicked(virtualServerId: string) {
      const virtualServers = new Set(
        [...clientForm.virtualServers].filter(
          value => !equals(value.id, virtualServerId),
        ),
      )
      setClientForm({
        ...clientForm,
        virtualServers,
      });
    }

    const isCumulativeLimitsValid = validateCumulativeOrderValueLimitInJPY(clientForm.cumulativeOrderValueLimitInJPY);

    return (
      <form
        onSubmit={(event: FormEvent) => {
          event.stopPropagation();
          event.preventDefault();
          checkCumulativeLimitValues(clientForm.cumulativeOrderValueLimitInJPY, clientForm);
          clientForm.readyForConfirmation
            ? clientFormSubmitted(clientForm)
            : setClientForm({ ...clientForm, readyForConfirmation: isCumulativeLimitsValid })
        }}
      >
        <fieldset
          disabled={clientForm.awaitingSaveResult}
          className='no-border'
        >
          <ClientInfo
            clientName={clientForm.name}
            clientCode={clientForm.code}
            clientIsActive={clientForm.isActive}
            virtualServerGroupId={clientForm.virtualServerGroupId}
            virtualServers={clientForm.virtualServers}
            alerts={clientForm.alerts}
            onClientNameChange={(value) =>
              setClientForm({ ...clientForm, name: value })
            }
            onClientCodeChange={(value) =>
              setClientForm({ ...clientForm, code: value })
            }
            onClientIsActiveClicked={(value) =>
              setClientForm({ ...clientForm, isActive: value })
            }
            onClientVirtualServerGroupIdChange={(value) =>
              setClientForm({ ...clientForm, virtualServerGroupId: value })
            }
          />
          <hr/>
          <h2>Limits</h2>
          <RCGrid
            title={'RC1 Total Clip Size'}
            clientForm={clientForm}
            alerts={getAlertsForRC(clientForm.alerts, 'RC1_total_clip_size')}
            limitName={'RC1'}
            limitForRC={clientForm.tcsLimitInJPY}
            limitChangedEvent={(value) =>
              setClientForm({ ...clientForm, tcsLimitInJPY: value })
            }
            alertChangedEvent={alertChanged}
            displayManageContactsOverlay={displayManageContactsOverlay}
          />
          <RCGrid
            title={'RC2 Exposure Limit (JPY)'}
            clientForm={clientForm}
            alerts={getAlertsForRC(clientForm.alerts, 'RC2_exposure')}
            limitName={'RC2'}
            limitForRC={clientForm.exposureLimitInJpy}
            limitChangedEvent={(value) =>
              setClientForm({ ...clientForm, exposureLimitInJpy: value })
            }
            alertChangedEvent={alertChanged}
            displayManageContactsOverlay={displayManageContactsOverlay}
          />
          <RCGrid
            title={'Realistic Exposure Value Limit %'}
            clientForm={clientForm}
            alerts={getAlertsForRC(
              clientForm.alerts,
              'Realistic_exposure_value',
            )}
            limitName={'REV'}
            limitForRC={clientForm.realisticExposureLimit}
            limitChangedEvent={(value) =>
              setClientForm({ ...clientForm, realisticExposureLimit: value })
            }
            alertChangedEvent={alertChanged}
            displayManageContactsOverlay={displayManageContactsOverlay}
          />
          <RCGrid
            title={'RC6 Unactioned Orders'}
            clientForm={clientForm}
            alerts={getAlertsForRC(clientForm.alerts, 'RC6_unactioned_orders')}
            limitName={'RC6'}
            limitForRC={clientForm.unactionedOrdersLimit}
            limitChangedEvent={(value) =>
              setClientForm({ ...clientForm, unactionedOrdersLimit: value })
            }
            alertChangedEvent={alertChanged}
            displayManageContactsOverlay={displayManageContactsOverlay}
          />
          <CumulativeLimitGrid
            title='Order Value'
            mandatory={true}
            limitValue={clientForm.cumulativeOrderValueLimitInJPY}
            intervalValue={clientForm.cumulativeOrderValueTimeIntervalSeconds}
            setLimitFunction={(value) =>
              setClientForm({
                ...clientForm,
                cumulativeOrderValueLimitInJPY: value,
              })
            }
            setIntervalFunction={(value) =>
              setClientForm({
                ...clientForm,
                cumulativeOrderValueTimeIntervalSeconds: value,
              })
            }
          />
          <CumulativeLimitGrid
            title={'Execution Value'}
            mandatory={false}
            limitValue={clientForm.cumulativeExecutionValueLimitInJPY}
            intervalValue={
              clientForm.cumulativeExecutionValueTimeIntervalSeconds
            }
            setLimitFunction={(value) =>
              setClientForm({
                ...clientForm,
                cumulativeExecutionValueLimitInJPY: value,
              })
            }
            setIntervalFunction={(value) =>
              setClientForm({
                ...clientForm,
                cumulativeExecutionValueTimeIntervalSeconds: value,
              })
            }
          />
          <hr/>
          <div>
            <h2
              style={{ display: 'inline-block' }}
              className='margin-right-1-em'
            >
              Manage Virtual Servers
            </h2>
            <ManageContactsButton
              limitName={'Virtual Server'}
              severity={'Breach'}
              clientForm={clientForm}
              disconnectedAlert={
                clientForm.alerts.filter(
                  (a) => a.limitType === 'Virtual_server_suspension',
                )[0]
              }
              displayManageContactsOverlay={displayManageContactsOverlay}
            />
          </div>
          <VirtualServersGrid
            virtualServers={clientForm.virtualServers}
            addVirtualServerClicked={(value) =>
              addVirtualServerClicked(value, clientForm)
            }
            removeVirtualServerClicked={(value) =>
              removeVirtualServerClicked(value)
            }
          />
          {clientForm.readyForConfirmation && !clientForm.awaitingSaveResult ? (
            <div className='text-center margin-top-2-em'>
              <label>
                <em>
                  {clientHasExchangeOrVirtualServerChanges(clientForm)
                    ? 'Click again to save your changes and update the exchange'
                    : forceUpdateExchangeLimit
                      ? 'Click again to save your changes and update the exchange limits'
                      : 'Click again to save your changes'}
                </em>
              </label>
            </div>
          ) : (
            <></>
          )}
          {clientForm.awaitingSaveResult ? (
            <button className='text-center button button--full_width margin-top-2-em'>
              <div className='loader'/>
            </button>
          ) : (
            <button className='theme--primary button button--full_width margin-top-2-em fat-padding'>
              {clientForm.readyForConfirmation ? (
                <b>
                  <em>
                    {clientHasExchangeOrVirtualServerChanges(clientForm)
                      ? 'Save Changes and Update Exchange Limits'
                      : forceUpdateExchangeLimit
                        ? 'Save Changes and Force Update Exchange Limits'
                        : 'Save Changes'}
                  </em>
                </b>
              ) : clientHasExchangeOrVirtualServerChanges(clientForm) ? (
                'Proceed To Confirmation (Exchange limits will be set)'
              ) : (
                'Proceed To Confirmation'
              )}
            </button>
          )}
          {clientForm.readyForConfirmation || addingClient ? (
            <></>
          ) : (
            <b>
              <em>
                {clientHasExchangeOrVirtualServerChanges(clientForm) || (
                  <button
                    className='theme--primary button button--full_width margin-top-2-em fat-padding force-limit-update'
                    onClick={() => setForceUpdateExchangeLimit(clientForm.isActive)}
                  >
                    Force Limit Update
                  </button>
                )}
              </em>
            </b>
          )}
        </fieldset>
      </form>
    )
  }

  const manageContactsForm = clientForm?.manageContactsForm;
  return (
    <div className={gridClass}>
      <div className='client-selector-list'>
        <ClientsList clientConfigs={clientConfigs} clientForms={clientForms} setAddingClient={setAddingClient}/>
      </div>

      {clientForm !== undefined
        ? getClientForm(clientForm)
        : selectSomethingMessage}
      {clientForm != undefined && manageContactsForm !== undefined ? <ContactsForm
        manageContactsForm={manageContactsForm}
        addContactClicked={(value) => handleAddContactClicked(value, clientForm, manageContactsForm)}
        removeContactClicked={(value) => removeContactClicked(clientForm, manageContactsForm, value)}
      /> : null}
    </div>
  )
}

function preventSubmitOnEnter(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.key === 'Enter') {
    e.preventDefault()
  }
}
